<template>
  <settings-form :breadcrumbs="useSettingsBreadcrumbs(section.name || $t('label.section'))">
    <template #actions>
      <v-btn
        class="mr-2"
        color="success"
        :disabled="!isChanged"
        @click="save"
      >
        <v-icon>mdi-check</v-icon>
        {{ $t('button.save_changes') }}
      </v-btn>
      <v-btn
        class="mr-2"
        color="error"
        :disabled="isNew"
        @click="deleteConfirmation = true"
      >
        <v-icon>mdi-delete</v-icon>
        <span class="d-none d-lg-block">{{ $t('button.delete_section') }}</span>
      </v-btn>
    </template>
    <template #content>
      <!-- Tab Allocations Dialog -->
      <tab-allocations-dialog
        :visible="allocationsDialog"
        :mode="allocationsDialogMode"
        @close="allocationsDialog = false"
      />

      <!-- Delete Confirmation Dialog -->
      <confirmation-dialog
        :visible="deleteConfirmation"
        :message="$t('message.confirm_delete_section')"
        @confirm="remove"
        @close="deleteConfirmation = false"
      />

      <!-- Manage Tables Dialog -->
      <tables-dialog
        :visible="tablesDialog"
        :tab="section"
        @close="closeDialogs()"
      />

      <floorplan-edit-dialog
        :visible="floorplanDialog"
        :tab="section"
        @close="closeDialogs()"
      />

      <!-- Manage Duration Rules Dialog -->
      <duration-rules-dialog
        :visible="durationRulesDialog"
        :section="section"
        @close="durationRulesDialog = false"
      />

      <!--  -->
      <validation-observer ref="observerRef">
        <settings-group
          :title="$t('title.edit_section')"
          :subtitle="$t('settings.section.edit_section_subtitle')"
          gutter
        >
          <v-col
            cols="12"
            class="pb-0"
          >
            <validated-text-field
              v-model="section.name"
              :label="$t('label.section_name')"
              rules="required|min:2|max:255"
            />
          </v-col>
          <v-col
            v-if="section.usingWeekdaysAndTimes"
            cols="12"
          >
            <p class="subheading v-label mb-2">
              {{ $t('label.online_capacity') }}
            </p>
            <v-slider
              v-model="section.capacity"
              :disabled="!section.usingWeekdaysAndTimes || maxCapacity === 0"
              class="align-center"
              :max="maxCapacitySlider"
              min="0"
              inverse-label
              :label="section.capacity !== 0 ? section.capacity : '0'"
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            class="pt-0"
          >
            <v-switch
              v-model="section.usingWeekdaysAndTimes"
              class="py-0"
              :label="$t('label.online')"
              hide-details
              @change="updateCapacity"
            />
          </v-col>
        </settings-group>
        <settings-group
          :title="$t('label.tables')"
          :subtitle="$t('settings.section.tables_subtitle')"
          gutter
        >
          <v-col
            cols="12"
            sm="6"
          >
            <v-btn
              outlined
              block
              class="my-1"
              color="primary"
              :disabled="isNew"
              @click="tablesDialog = true"
            >
              {{ $t('button.setup_tables') }}
            </v-btn>
          </v-col>
          <v-col
            cols="12"
            sm="6"
          >
            <v-btn
              class="my-1"
              outlined
              block
              :disabled="isNew"
              color="primary"
              @click="floorplanDialog = true"
            >
              {{ $t('button.setup_floorplan') }}
            </v-btn>
          </v-col>
        </settings-group>
        <settings-group
          :title="$t('label.allocations')"
          :subtitle="$t('settings.section.allocations_subtitle')"
          gutter
        >
          <v-col cols="12">
            <v-btn
              class="my-1"
              outlined
              block
              :disabled="isNew"
              color="primary"
              @click="showAllowedAllocationsDialog"
            >
              {{ $t('button.preferred_allocations') }}
            </v-btn>
            <div class="d-inline ml-5" />
            <v-btn
              class="my-1"
              outlined
              block
              :disabled="isNew"
              color="primary"
              @click="showDisallowedAllocationsDialog"
            >
              {{ $t('button.disallowed_allocations') }}
            </v-btn>
          </v-col>
        </settings-group>
        <settings-group
          :title="$t('label.seating_time')"
          :subtitle="$t('settings.section.seating_time_subtitle')"
          gutter
        >
          <v-col
            cols="12"
            sm="6"
            class="pt-0"
          >
            <v-select
              v-model="section.duration"
              :items="seatingTimes"
              :label="$t('label.default_seating_time')"
              @input="changeSeatingTime"
            />
          </v-col>
          <v-col
            class="text-center pt-0 pb-5 pb-md-0 pt-sm-1"
            sm="6"
            cols="12"
          >
            <v-btn
              outlined
              block
              color="primary"
              :disabled="isNew"
              @click="durationRulesDialog = true"
            >
              {{ $t('button.setup_additional_seating_time') }}
            </v-btn>
          </v-col>
          <v-col
            cols="12"
            class="pt-0"
          >
            <v-select
              v-model="section.minDuration"
              :items="minSeatingTimes"
              :label="$t('label.min_seating_time')"
            />
          </v-col>
          <v-col
            cols="12"
            class="pt-0"
          >
            <v-select
              v-model="section.bookingCap"
              :items="bookingCaps"
              :label="$t('label.booking_cap')"
            />
          </v-col>
        </settings-group>
      </validation-observer>
    </template>
  </settings-form>
</template>

<script setup lang="ts">
import {
  ref, computed, watch, getCurrentInstance, onMounted, onBeforeUnmount,
} from 'vue';
import { useSettingsBreadcrumbs } from '@/mixins/settings-breadcrumbs';
import DurationRulesDialog from '@/components/views/settings/DurationRulesDialog.vue';
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue';
import TablesDialog from '@/components/views/settings/TablesDialog.vue';
import FloorplanEditDialog from '@/components/views/settings/FloorplanEditDialog.vue';
import TabAllocationsDialog, { AllocationsDialogMode } from '@/components/views/settings/TabAllocationsDialog.vue';
import Tab from '@/model/Tab';
import { cloneModel, isModelEqualToModel } from '@/model/model-utils';
import { performSaveAction, performAction } from '@/services/vue-utils';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import { loadStoreModule, unloadStoreModule } from '@/services/module-loader';
import AllocationsStore from '@/store/allocations-store';
import SettingsGroup from '@/components/layouts/SettingsGroup.vue';
import useStoreWatch from '@/composables/store-watch';
import { useRoute, useRouter } from 'vue-router/composables';
import store from '@/store/store';
import i18n from '@/plugins/i18n';
import useUnsavedGuard from '@/composables/unsaved-guard';
import { ValidationObserver } from 'vee-validate';
import { useSeatingTimeItems, useBookingCaps } from './settings/helpers/settings-values';

const observerRef = ref(null as null | InstanceType<typeof ValidationObserver>);

const router = useRouter();
const route = useRoute();

const section = ref(new Tab());
const oldSection = ref(new Tab());

const seatingTimes = useSeatingTimeItems();
const bookingCaps = useBookingCaps();
const minSeatingTimes = computed(() => {
  const st = seatingTimes.value.filter((o) => o.value <= (section.value.duration ?? 0));
  const mst = [{ value: 0, text: i18n.tc('label.default_seating_time') }, ...st];
  return mst;
});

// load data
const reset = () => {
  const id = Number(router.currentRoute.params.id) || 0;
  const s = store.state.settings.tabs.find((o) => o.id === id) ?? new Tab();
  section.value = cloneModel(s);
  oldSection.value = s;
};
useStoreWatch(reset, true);

const isNew = computed(() => !section.value.id);
const isChanged = computed(() => !isModelEqualToModel(section.value, oldSection.value));
useUnsavedGuard(isChanged);

const loadAllocations = () => store.dispatch('loadAllocations');
const resetAllocations = () => store.dispatch('resetAllocations');

onMounted(() => {
  loadStoreModule(getCurrentInstance()?.proxy!, 'allocations', AllocationsStore);
});
onBeforeUnmount(() => {
  unloadStoreModule(getCurrentInstance()?.proxy!, 'allocations', async () => { resetAllocations(); });
});

// Dialogs
const deleteConfirmation = ref(false);
const tablesDialog = ref(false);
const floorplanDialog = ref(false);
const allocationsDialog = ref(false);
const allocationsDialogMode = ref(AllocationsDialogMode.AllowedAllocations);
const durationRulesDialog = ref(false);

const showAllocationsDialog = async (mode: AllocationsDialogMode) => {
  allocationsDialogMode.value = mode;
  const ok = await performAction(
    null,
    i18n.tc('error.load_allocations'),
    async () => store.dispatch('loadAllocations', { tabId: section.value.id }),
  );
  if (ok) allocationsDialog.value = true;
};

const showAllowedAllocationsDialog = async () => {
  showAllocationsDialog(AllocationsDialogMode.AllowedAllocations);
};

const showDisallowedAllocationsDialog = async () => {
  showAllocationsDialog(AllocationsDialogMode.DisallowedAllocations);
};

const closeDialogs = () => {
  tablesDialog.value = false;
  floorplanDialog.value = false;
  reset();
};

watch(() => route.name, () => {
  if (route.query.floorplan) {
    floorplanDialog.value = true;
  } else if (route.query.tables) {
    tablesDialog.value = true;
  }
});

// slider
const maxCapacity = computed(() => section.value.tabItems.map((o) => o.seats).reduce((a, b) => a + b, 0));
const maxCapacitySlider = computed(() => (maxCapacity.value === 0 ? 100 : maxCapacity.value));

const updateCapacity = (onlineValue: boolean) => {
  if (!onlineValue) return;
  section.value.capacity = maxCapacity.value;
};

const changeSeatingTime = (value: number) => {
  section.value.duration = value;
  if (section.value.minDuration >= value) {
    section.value.minDuration = 0;
  }
};

// save delete
const save = async () => {
  const entities = store.state.settings.tabs.map(
    (o) => (o.id === section.value.id ? section.value : cloneModel(o)),
  );
  const ok = await performSaveAction(
    observerRef.value,
    async () => store.dispatch('sendTabs', { entities }),
  );

  if (ok) {
    reset();
    router.back();
  }
};

const remove = async () => {
  const entities = store.state.settings.tabs.map((o) => cloneModel(o));
  const index = entities.findIndex((o: Tab) => o.id === section.value.id);
  if (index >= 0) entities.splice(index, 1);
  const ok = await performAction(
    i18n.tc('message.success_section_deleted'),
    i18n.tc('error.deleting_section'),
    async () => store.dispatch('sendTabs', { entities }),
  );
  if (ok) {
    reset();
    router.back();
  }
};

</script>
